
import { windowQuads, type CellWindow } from "./window";
import { clamp } from "../clamp";

export const INSTANCE_BYTES = 12;

export const OFFSET_CELL = 0;
export const OFFSET_LAYERS = 4;
export const OFFSET_BLOCKED = 8;

/** Corner order, shared by the layer and blocked quads. It matches the
 *  unit quad's corners so the shader's bilinear weights line up with it. */
const CORNERS: readonly (readonly [number, number])[] = [
  [0, 0],
  [1, 0],
  [0, 1],
  [1, 1],
];

/** What the builder needs from the tile map. */
export interface TileSource {
  readonly width: number;
  readonly height: number;
  shaderIndexAt(cellX: number, cellY: number): number;
}

/** What the builder needs from the collision map: the raw flag grid, world
 *  row-major, which is the same grid and the same order as the tile indices. */
export interface AttributeSource {
  readonly worldCellsX: number;
  readonly worldCellsY: number;
  readonly cells: Uint8Array;
}

/** One instance per quad. Named separately from `windowQuads` because this is
 *  the buffer's length and that is the window's geometry, but there is only ever
 *  one answer and only one place it is computed. */
export function instanceCount(window: CellWindow): number {
  return windowQuads(window);
}

export function buildInstances(
  window: CellWindow,
  tiles: TileSource,
  attributes: AttributeSource,
  blockedFlag: number,
  out: Uint8Array,
): number {
  const quads = instanceCount(window);
  if (out.byteLength < quads * INSTANCE_BYTES) {
    throw new RangeError(
      `instance buffer holds ${Math.floor(out.byteLength / INSTANCE_BYTES)} quads, needs ${quads}`,
    );
  }
  if (quads === 0) return 0;

  const view = new DataView(out.buffer, out.byteOffset, out.byteLength);
  let at = 0;

  for (let cellY = window.y0; cellY < window.y1 - 1; cellY++) {
    for (let cellX = window.x0; cellX < window.x1 - 1; cellX++) {
      view.setUint16(at + OFFSET_CELL, cellX, true);
      view.setUint16(at + OFFSET_CELL + 2, cellY, true);

      for (let corner = 0; corner < CORNERS.length; corner++) {
        const offset = CORNERS[corner];
        const tx = clamp(cellX + offset[0], 0, tiles.width - 1);
        const ty = clamp(cellY + offset[1], 0, tiles.height - 1);
        out[at + OFFSET_LAYERS + corner] = tiles.shaderIndexAt(tx, ty);

        const ax = clamp(cellX + offset[0], 0, attributes.worldCellsX - 1);
        const ay = clamp(cellY + offset[1], 0, attributes.worldCellsY - 1);
        const flags = attributes.cells[ay * attributes.worldCellsX + ax];
        // 0 or 255 so the attribute can be uploaded normalized and arrive in the
        // shader as a clean 0..1 the bilinear weights can mix.
        out[at + OFFSET_BLOCKED + corner] = (flags & blockedFlag) !== 0 ? 255 : 0;
      }
      at += INSTANCE_BYTES;
    }
  }
  return quads;
}
